/**
 * EditorialScaleMeter — a thin horizontal gradient scale with a single
 * marker, used for go↔no-go / opposed↔favourable / shortfall gauges.
 *
 * Editorial reference: `.band .scale` (Exec D2) + `.sent` (Sentiment 12A) +
 *   `.gauge .track` (Power adequacy A) across the round3 mockups:
 *     a thin gradient bar (nogo → cond → go), a vertical ink marker at the
 *     normalised position, and low/mid/high tick labels beneath.
 *
 * Used by:
 *   - Executive Summary (D2 decision band — go/no-go scale)
 *   - Public Sentiment (12A favourable↔opposed gauge)
 *   - Power Availability (4A adequacy gauge — `fill` variant; see below)
 *
 * Two variants:
 *   'gradient' — a nogo→cond→go gradient track with a marker (Exec D2 / Sent 12A).
 *   'fill'     — a solid accent fill to `value` with an optional need marker
 *                (Power adequacy: firm vs need).
 *
 * Theme tokens read: 'negative'/'warning'/'positive' (gradient stops),
 * 'ink' (marker), 'faint' (ticks), 'accent' (fill + fill label bg),
 * 'subtleBg' (fill track), 'negative' (need marker). Mono ticks via
 * --report-body-font. Pure / print-safe.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { BAR_HEIGHT } from '../../../../lib/reportChartLayoutTokens';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

export interface EditorialScaleMeterProps {
  theme: ReportTheme;
  variant?: 'gradient' | 'fill';
  /** Marker / fill position, 0..1. */
  value: number;
  /** Tick labels beneath, left→right. */
  ticks?: React.ReactNode[];
  /** Caption line under the scale (e.g. the verdict line). */
  caption?: React.ReactNode;
  /** Label inside the fill (fill variant). */
  fillLabel?: React.ReactNode;
  /** Need / target marker (fill variant), 0..1, with an optional label. */
  needMarker?: { at: number; label?: React.ReactNode };
  /**
   * Label inside the un-filled remainder (fill variant) — names the shortfall
   * region the way the reference page-5 grid-gap bar reads ("BTM generation
   * gap"). Rendered ONLY when the fill leaves a visible remainder (> ~6%), so
   * a full / over-supplied bar never shows an empty "gap" caption.
   */
  shortfallLabel?: React.ReactNode;
  /**
   * Default height (px) is variant-aware via the tokenized BAR_HEIGHT
   * (gradient = thin go/no-go rule; fill = a taller adequacy/shortfall block);
   * pass to override.
   */
  height?: number;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function EditorialScaleMeter({
  theme,
  variant = 'gradient',
  value,
  ticks,
  caption,
  fillLabel,
  needMarker,
  shortfallLabel,
  // K4 — variant-aware tokenized default: thin go/no-go rule vs taller
  // adequacy/shortfall block. An explicit `height` still overrides.
  height = variant === 'fill' ? BAR_HEIGHT.scaleFill : BAR_HEIGHT.scaleGradient,
  id,
  className,
  style,
}: EditorialScaleMeterProps): React.ReactElement {
  const ink = roleColor(theme, 'ink');
  const faint = roleColor(theme, 'faint');
  const negative = roleColor(theme, 'negative');
  const accent = roleColor(theme, 'accent');
  const pos = Math.max(0, Math.min(1, value)) * 100;
  // K4 — the un-filled remainder (the shortfall) reads as an explicit hatched
  // region (the reference page-5 "BTM generation gap"), drawn behind the fill
  // so the bar shows BOTH the available portion (solid accent) and the gap
  // (diagonal hatch). The hatch is a CSS repeating-linear-gradient in the
  // theme's negative tone @ low alpha — honest geometry, theme-tokenized, no
  // raster asset. Only meaningful when there's a visible remainder.
  const remainderPct = Math.max(0, 100 - pos);
  const showShortfall = variant === 'fill' && remainderPct > 6;
  const hatch = `repeating-linear-gradient(45deg, ${roleColorAlpha(theme, 'negative', 0.16)} 0, ${roleColorAlpha(theme, 'negative', 0.16)} 3px, transparent 3px, transparent 7px)`;

  const gradient =
    variant === 'gradient'
      ? `linear-gradient(90deg, ${negative} 0%, ${roleColor(theme, 'warning')} 50%, ${roleColor(theme, 'positive')} 100%)`
      : undefined;

  return (
    <div id={id} className={className} style={style}>
      <div
        style={{
          position: 'relative',
          height,
          borderRadius: variant === 'gradient' ? height / 2 : 0,
          background: variant === 'gradient' ? gradient : roleColor(theme, 'subtleBg'),
          border: variant === 'fill' ? `1px solid ${roleColor(theme, 'hair')}` : undefined,
          // Clip the layered fill/hatch to the track ONLY for the fill variant;
          // the gradient variant's marker deliberately overhangs the rule.
          overflow: variant === 'fill' ? 'hidden' : undefined,
        }}
      >
        {/* K4 — hatched shortfall region (the un-filled remainder), drawn first
             so the solid available fill layers over its left edge. Reads as the
             reference's diagonally-hatched "generation gap". */}
        {showShortfall && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: `${remainderPct}%`,
              background: hatch,
              borderLeft: `1px dashed ${roleColorAlpha(theme, 'negative', 0.5)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: 10,
              color: roleColor(theme, 'negative'),
              fontFamily: MONO,
              fontSize: REPORT_TEXT_PX.caption,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {shortfallLabel}
          </div>
        )}
        {variant === 'fill' && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${pos}%`,
              background: accent,
              display: 'flex',
              alignItems: 'center',
              paddingLeft: 10,
              color: '#ffffff',
              fontFamily: MONO,
              fontSize: REPORT_TEXT_PX.label,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {fillLabel}
          </div>
        )}
        {variant === 'gradient' && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: -6,
              left: `${pos}%`,
              width: 2,
              height: height + 12,
              background: ink,
              transform: 'translateX(-1px)',
            }}
          />
        )}
        {needMarker && Number.isFinite(needMarker.at) && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: -6,
              bottom: -6,
              left: `${Math.max(0, Math.min(1, needMarker.at)) * 100}%`,
              width: 2,
              background: negative,
            }}
          />
        )}
      </div>
      {needMarker?.label != null && (
        <div
          style={{
            fontFamily: MONO,
            fontSize: REPORT_TEXT_PX.micro,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: negative,
            marginTop: 4,
          }}
        >
          {needMarker.label}
        </div>
      )}
      {ticks && ticks.length > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: MONO,
            fontSize: REPORT_TEXT_PX.micro,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: faint,
            marginTop: 9,
          }}
        >
          {ticks.map((t, i) => (
            <span key={i}>{t}</span>
          ))}
        </div>
      )}
      {caption != null && (
        <div
          style={{
            fontFamily: MONO,
            fontSize: REPORT_TEXT_PX.annotation,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: roleColor(theme, 'warning'),
            fontWeight: 600,
            marginTop: 10,
            textAlign: 'center',
          }}
        >
          {caption}
        </div>
      )}
    </div>
  );
}
